import { useCallback, useState } from 'react';
import { trackingService, type MatchRouteResponse } from '../services/trackingService';

/**
 * Orchestrates the OSRM path-correction workflow for a single segment at
 * a time. Keeps the last computed / applied preview in state so the map
 * can overlay the corrected polyline while the reviewer decides.
 *
 * Documented workflow (docs/location-tracking/osrm-optimization.md §"Typical Workflow"):
 *
 *   1. `computeMatch(segmentId, extend?)` → GET /match-route
 *   2. Review returned `osrm_path` + `osrm_total_distance`.
 *   3. `applyMatch(...)` or `rejectMatch(...)` → POST /insert-osrm-path.
 *   4. `syncSummary(userId, date)` → POST /sync-osrm-data (must be a
 *      past date; today / future are rejected server-side).
 */

/** Preview state kept between compute → apply/reject. */
export interface OsrmPreview {
  segmentId: number;
  /** `true` when the caller asked for `extend=true`; kept so the panel
   *  can label the preview accurately. */
  extended: boolean;
  path: [number, number][];
  distanceMeters: number;
  message: string;
  /** `apply` / `reject` decision once the user commits. Left `null`
   *  while the preview is still under review. */
  decision: 'apply' | 'reject' | null;
}

export interface OsrmError {
  status?: number;
  message: string;
  code?: string;
}

/**
 * Result union for `computeMatch`. Returning the error object here
 * (rather than only setting hook state) lets callers pick a toast copy
 * that reflects the specific failure — e.g. the "gap must be ≥ 1 km"
 * validation the server enforces per `match_route.min_gap_distance_meters`.
 */
export type MatchResult =
  | { ok: true; data: MatchRouteResponse }
  | { ok: false; error: OsrmError };

function unwrapError(err: unknown, fallback: string): OsrmError {
  const anyErr = err as {
    response?: { status?: number; data?: { message?: string; error?: string } };
    message?: string;
  };
  return {
    status: anyErr?.response?.status,
    message:
      anyErr?.response?.data?.message ||
      anyErr?.response?.data?.error ||
      anyErr?.message ||
      fallback,
    code: anyErr?.response?.data?.error,
  };
}

export const useOsrmCorrection = () => {
  const [preview, setPreview] = useState<OsrmPreview | null>(null);
  const [busy, setBusy] = useState<
    'match' | 'apply' | 'reject' | 'sync' | null
  >(null);
  const [error, setError] = useState<OsrmError | null>(null);

  const clearPreview = useCallback(() => {
    setPreview(null);
    setError(null);
  }, []);

  const computeMatch = useCallback(
    async (segmentId: number, options: { extend?: boolean } = {}): Promise<MatchResult> => {
      setBusy('match');
      setError(null);
      try {
        const res = await trackingService.matchRoute(segmentId, options);
        setPreview({
          segmentId,
          extended: !!options.extend,
          path: res.osrm_path,
          distanceMeters: res.osrm_total_distance,
          message: res.message,
          decision: null,
        });
        return { ok: true, data: res };
      } catch (err) {
        const e = unwrapError(err, 'Could not compute the corrected path.');
        setError(e);
        return { ok: false, error: e };
      } finally {
        setBusy(null);
      }
    },
    [],
  );

  const decide = useCallback(
    async (action: 'apply' | 'reject'): Promise<boolean> => {
      const current = preview;
      if (!current) return false;
      setBusy(action);
      setError(null);
      try {
        await trackingService.setOsrmPathAction(current.segmentId, action);
        setPreview((p) => (p ? { ...p, decision: action } : p));
        return true;
      } catch (err) {
        setError(unwrapError(err, `Could not ${action} the corrected path.`));
        return false;
      } finally {
        setBusy(null);
      }
    },
    [preview],
  );

  const applyMatch = useCallback(() => decide('apply'), [decide]);
  const rejectMatch = useCallback(() => decide('reject'), [decide]);

  const syncSummary = useCallback(
    async (userId: string, date: string): Promise<boolean> => {
      setBusy('sync');
      setError(null);
      try {
        await trackingService.syncOsrmData(userId, date);
        return true;
      } catch (err) {
        setError(unwrapError(err, 'Could not sync the daily summary.'));
        return false;
      } finally {
        setBusy(null);
      }
    },
    [],
  );

  return {
    preview,
    busy,
    error,
    computeMatch,
    applyMatch,
    rejectMatch,
    syncSummary,
    clearPreview,
  };
};
